import { Part, Flange, FlangeId, EdgeKey, MaterialKey, Hole, HoleId, DimMode } from './types';
import { nearestGauge } from './materials';
import { outsideSetback, insideSetback } from './bendMath';

// Monotonically-increasing counter combined with timestamp + random, so IDs are
// unique even when many are generated within the same millisecond, while still
// being short enough to read in saved JSON.
let _idCounter = 0;
export function newId(prefix = 'f'): FlangeId {
  _idCounter = (_idCounter + 1) & 0xffff;
  const t = Date.now().toString(36);
  const c = _idCounter.toString(36).padStart(3, '0');
  const r = Math.random().toString(36).slice(2, 6);
  return `${prefix}_${t}${c}_${r}`;
}

export function defaultPart(material: MaterialKey = 'mild'): Part {
  const thickness = 1.0;
  const gauge = nearestGauge(material, thickness);
  const rootId = newId('root');
  // Default base size of 500×500 with `outside` dim mode reflects the typical
  // workflow at the shop: design from outside dimensions of a sheet plate.
  const root: Flange = {
    id: rootId,
    name: 'Base',
    length: 500,
    width: 500,
    children: [],
  };
  return {
    thickness,
    material,
    kFactor: gauge.kFactor,
    miterGap: 1.0,
    dimMode: 'outside',
    rootId,
    flanges: { [rootId]: root },
    holes: [],
  };
}

export function addHole(
  part: Part,
  flangeId: FlangeId,
  opts: Partial<Omit<Hole, 'id' | 'flangeId'>> = {},
): Part {
  const f = part.flanges[flangeId];
  if (!f) return part;
  const id = newId('h');
  const defaultDiameter = Math.max(part.thickness * 2, 3);
  const hole: Hole = {
    id,
    flangeId,
    kind: opts.kind ?? 'round',
    x: opts.x ?? f.length / 2,
    y: opts.y ?? f.width / 2,
    diameter: opts.diameter ?? defaultDiameter,
    length: opts.length,
    rotation: opts.rotation,
  };
  return { ...part, holes: [...part.holes, hole] };
}

export function deleteHole(part: Part, holeId: HoleId): Part {
  return { ...part, holes: part.holes.filter((h) => h.id !== holeId) };
}

export function updateHole(part: Part, holeId: HoleId, patch: Partial<Hole>): Part {
  return {
    ...part,
    holes: part.holes.map((h) => (h.id === holeId ? { ...h, ...patch, id: h.id, flangeId: h.flangeId } : h)),
  };
}

export function addFlange(
  part: Part,
  parentId: FlangeId,
  edge: EdgeKey,
  opts: {
    length?: number;
    width?: number;
    offset?: number;
    bendAngle?: number;
    bendRadius?: number;
    miterCorner?: boolean;
  } = {},
): Part {
  const parent = part.flanges[parentId];
  if (!parent) return part;
  const gauge = nearestGauge(part.material, part.thickness);
  const id = newId('f');
  const edgeLen = edge === '+x' || edge === '-x' ? parent.width : parent.length;
  const childWidth = opts.width ?? edgeLen;
  const offset = opts.offset ?? 0;
  const child: Flange = {
    id,
    name: `Flange ${Object.keys(part.flanges).length}`,
    length: opts.length ?? 30,
    width: childWidth,
    attachment: {
      parentId,
      edge,
      offset,
      bendAngle: opts.bendAngle ?? 90,
      bendRadius: opts.bendRadius ?? gauge.defaultBendRadius,
      miterCorner: opts.miterCorner ?? false,
    },
    children: [],
  };
  return {
    ...part,
    flanges: {
      ...part.flanges,
      [parentId]: { ...parent, children: [...parent.children, id] },
      [id]: child,
    },
  };
}

export function deleteFlange(part: Part, id: FlangeId): Part {
  if (id === part.rootId) return part;
  const f = part.flanges[id];
  if (!f) return part;
  // collect descendants
  const toDelete = new Set<FlangeId>();
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    if (toDelete.has(cur)) continue;
    toDelete.add(cur);
    const c = part.flanges[cur];
    if (c) stack.push(...c.children);
  }
  const flanges = { ...part.flanges };
  for (const d of toDelete) delete flanges[d];
  // remove from parent's children
  if (f.attachment) {
    const p = flanges[f.attachment.parentId];
    if (p) flanges[f.attachment.parentId] = { ...p, children: p.children.filter((c) => c !== id) };
  }
  // remove holes attached to deleted flanges
  const holes = part.holes.filter((h) => !toDelete.has(h.flangeId));
  return { ...part, flanges, holes };
}

export function updateFlange(part: Part, id: FlangeId, patch: Partial<Flange>): Part {
  const f = part.flanges[id];
  if (!f) return part;
  return {
    ...part,
    flanges: {
      ...part.flanges,
      [id]: { ...f, ...patch, children: f.children },
    },
  };
}

export function updateAttachment(
  part: Part,
  id: FlangeId,
  patch: Partial<NonNullable<Flange['attachment']>>,
): Part {
  const f = part.flanges[id];
  if (!f || !f.attachment) return part;
  return updateFlange(part, id, { attachment: { ...f.attachment, ...patch } });
}

export function setMaterial(part: Part, material: MaterialKey): Part {
  const gauge = nearestGauge(material, part.thickness);
  return { ...part, material, kFactor: gauge.kFactor };
}

export function setThickness(part: Part, thickness: number): Part {
  const t = Math.max(0.1, thickness);
  const gauge = nearestGauge(part.material, t);
  return { ...part, thickness: t, kFactor: gauge.kFactor };
}

export type PresetKind = 'single' | 'zbend' | 'joggle' | 'hem';

export interface PresetResult {
  part: Part;
  // IDs of the flanges added by the preset, in creation order
  added: FlangeId[];
}

// Apply a 1-click preset to a parent's edge. The first child is added at the same
// edge in PARENT local space; any additional flanges chain off the previous one
// using its outward edge ('+x' in child local).
export function applyPreset(
  part: Part,
  parentId: FlangeId,
  edge: EdgeKey,
  preset: PresetKind,
): PresetResult {
  const T = part.thickness;
  const gauge = nearestGauge(part.material, T);
  const R = gauge.defaultBendRadius;
  const before = part;
  const added: FlangeId[] = [];

  const addAndTrack = (p: Part, parent: FlangeId, e: EdgeKey, opts: Parameters<typeof addFlange>[3]): Part => {
    const next = addFlange(p, parent, e, opts);
    if (next === p) return p;
    const newIds = Object.keys(next.flanges).filter((k) => !(k in p.flanges));
    if (newIds[0]) added.push(newIds[0]);
    return next;
  };

  let p = before;
  switch (preset) {
    case 'single': {
      p = addAndTrack(p, parentId, edge, { length: Math.max(15, T * 8), bendAngle: 90, bendRadius: R });
      break;
    }
    case 'zbend': {
      // Two perpendicular bends in opposite signs create a Z-shaped offset.
      // First flange = the "step" — keep it short. Second flange = the new run-out plane.
      const step = Math.max(8, T * 6);
      const runOut = Math.max(20, T * 12);
      p = addAndTrack(p, parentId, edge, { length: step, bendAngle: 90, bendRadius: R });
      const firstId = added[added.length - 1];
      if (firstId) {
        p = addAndTrack(p, firstId, '+x', { length: runOut, bendAngle: -90, bendRadius: R });
      }
      break;
    }
    case 'joggle': {
      // Two small bends of equal magnitude in opposite directions create a small
      // overlap step (used to nest joints). Default ~30°.
      const stepAngle = 30;
      const step = Math.max(4, T * 3);
      const runOut = Math.max(15, T * 8);
      p = addAndTrack(p, parentId, edge, { length: step, bendAngle: stepAngle, bendRadius: R });
      const firstId = added[added.length - 1];
      if (firstId) {
        p = addAndTrack(p, firstId, '+x', { length: runOut, bendAngle: -stepAngle, bendRadius: R });
      }
      break;
    }
    case 'hem': {
      // Closed hem: the flange is folded back ~175° onto itself. We use the smallest
      // viable radius for the material (0.5*T, capped) since hems are formed sharp.
      const hemLen = Math.max(6, T * 5);
      const hemR = Math.max(T * 0.5, 0.5);
      p = addAndTrack(p, parentId, edge, { length: hemLen, bendAngle: 175, bendRadius: hemR });
      break;
    }
  }

  return { part: p, added };
}

// Per-edge setback at one end of an axis. For child flanges the setback comes
// from the flange's own bend; for the root flange we look at children attached
// to that side (max OSSB/ISSB across walls on that side; multiple parallel walls
// don't add — they sit at different positions along the perpendicular axis).
function sideOffset(part: Part, flange: Flange, side: '+x' | '-x' | '+y' | '-y', mode: DimMode): number {
  if (mode === 'tangent') return 0;
  let max = 0;
  for (const cid of flange.children) {
    const c = part.flanges[cid];
    if (!c?.attachment || c.attachment.edge !== side) continue;
    const off = mode === 'outside'
      ? outsideSetback(c.attachment.bendAngle, c.attachment.bendRadius, part.thickness)
      : insideSetback(c.attachment.bendAngle, c.attachment.bendRadius);
    if (off > max) max = off;
  }
  return max;
}

// Total offset to add to a flange's stored LENGTH (local +X axis) to get its
// outside/inside dimension. Handles both:
//   • child flanges — their own bend contributes the setback
//   • root flange — sum of setbacks from any walls on ±X edges
export function lengthOffsetForMode(part: Part, flange: Flange, mode: DimMode): number {
  if (mode === 'tangent') return 0;
  if (flange.attachment) {
    const a = flange.attachment;
    return mode === 'outside'
      ? outsideSetback(a.bendAngle, a.bendRadius, part.thickness)
      : insideSetback(a.bendAngle, a.bendRadius);
  }
  // Root flange: outside dim = base + walls on +X side + walls on −X side
  return sideOffset(part, flange, '+x', mode) + sideOffset(part, flange, '-x', mode);
}

// Total offset to add to a flange's stored WIDTH (local +Y axis) to get its
// outside/inside dimension. Only meaningful for the root flange — child width
// runs along the bend tangent direction so it doesn't extend past any bend.
export function widthOffsetForMode(part: Part, flange: Flange, mode: DimMode): number {
  if (mode === 'tangent' || flange.attachment) return 0;
  return sideOffset(part, flange, '+y', mode) + sideOffset(part, flange, '-y', mode);
}

// Switch the dimension mode AND re-anchor every flange so the user-visible
// length numbers stay the same in the new mode. This means the underlying
// (tangent) length adjusts → the 3D part visibly resizes when you toggle.
//
// Without this, toggling modes would only relabel inputs and leave the part
// unchanged — a confusing "did I just break it?" UX.
export function changeDimMode(part: Part, newMode: DimMode): Part {
  if (part.dimMode === newMode) return part;
  const oldFlanges = part.flanges;
  const newFlanges: Record<FlangeId, Flange> = {};

  // Pass 1: adjust each flange's OWN length and width to keep their displayed
  // values stable across the mode change.
  //   - Every flange's length absorbs its bend-setback delta (root only sees a
  //     non-zero delta if it has perpendicular walls).
  //   - Only root's width absorbs the perpendicular-walls delta.
  for (const id of Object.keys(oldFlanges)) {
    const f = oldFlanges[id];
    const lenAdj = lengthOffsetForMode(part, f, part.dimMode) - lengthOffsetForMode(part, f, newMode);
    const widAdj = widthOffsetForMode(part, f, part.dimMode) - widthOffsetForMode(part, f, newMode);
    newFlanges[id] = {
      ...f,
      length: Math.max(0.1, roundMm(f.length + lenAdj)),
      width: Math.max(0.1, roundMm(f.width + widAdj)),
    };
  }

  // Pass 2: cascade. When a parent's dimension changed, scale the spans of its
  // children that ride on that axis (their width and offset) so they continue
  // to fit within the new parent edge — otherwise walls that previously covered
  // the full edge would overflow and the corners look wrong.
  //
  // Object.keys preserves insertion order, and addFlange always inserts a child
  // after its parent, so a parent is always updated before its children here.
  for (const id of Object.keys(newFlanges)) {
    const child = newFlanges[id];
    if (!child.attachment) continue;
    const parentOld = oldFlanges[child.attachment.parentId];
    const parentNew = newFlanges[child.attachment.parentId];
    if (!parentOld || !parentNew) continue;
    const isXEdge = child.attachment.edge === '+x' || child.attachment.edge === '-x';
    // The child's width runs along the parent's perpendicular dimension.
    const oldSpan = isXEdge ? parentOld.width : parentOld.length;
    const newSpan = isXEdge ? parentNew.width : parentNew.length;
    if (oldSpan < 1e-6 || Math.abs(oldSpan - newSpan) < 1e-6) continue;
    const scale = newSpan / oldSpan;
    newFlanges[id] = {
      ...child,
      width: Math.max(0.1, roundMm(child.width * scale)),
      attachment: {
        ...child.attachment,
        offset: Math.max(0, roundMm(child.attachment.offset * scale)),
      },
    };
  }

  return { ...part, dimMode: newMode, flanges: newFlanges };
}

// Round to 4 decimals (sub-micron precision). Without this, repeated mode
// toggles or edits accumulate float error and the input renders garbage like
// "5.499999999999998" or even longer strings.
function roundMm(v: number): number {
  return Math.round(v * 10000) / 10000;
}

// Display the flange's length in the user's chosen dimension mode.
export function flangeLengthDisplay(part: Part, flange: Flange): number {
  return roundMm(flange.length + lengthOffsetForMode(part, flange, part.dimMode));
}

// Convert a length entered by the user (in current dim mode) back to the
// internal tangent storage convention.
export function flangeLengthFromDisplay(part: Part, flange: Flange, displayValue: number): number {
  return Math.max(0.1, roundMm(displayValue - lengthOffsetForMode(part, flange, part.dimMode)));
}

// Width display/input — only the root flange has a width offset (child width
// runs along the bend axis and isn't extended by the bend).
export function flangeWidthDisplay(part: Part, flange: Flange): number {
  return roundMm(flange.width + widthOffsetForMode(part, flange, part.dimMode));
}

export function flangeWidthFromDisplay(part: Part, flange: Flange, displayValue: number): number {
  return Math.max(0.1, roundMm(displayValue - widthOffsetForMode(part, flange, part.dimMode)));
}

// DFS list of flange ids in tree order
export function flangeOrder(part: Part): FlangeId[] {
  const out: FlangeId[] = [];
  const walk = (id: FlangeId) => {
    out.push(id);
    const f = part.flanges[id];
    if (f) for (const c of f.children) walk(c);
  };
  walk(part.rootId);
  return out;
}
